import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { workspaceApi } from '../client/index.js'

/**
 * How many workspaces may stay OPEN at once. Opening one past the cap closes
 * the least-recently-used (a real `markClosed`, so it genuinely moves to
 * Recent) — the switcher's ACTIVE list therefore never grows unbounded, and
 * every card under ACTIVE is really open while every card under RECENT is
 * really closed. Enforced on OPEN only: a pre-existing over-cap list (opened
 * before this cap existed) is left alone rather than closing a user's
 * workspaces behind their back on boot; it self-trims the next time they open
 * one.
 */
export const MAX_OPEN_WORKSPACES = 6

export interface OpenWorkspace {
  id: string       // workspace DB id (used for API calls)
  folderId: string // .workpapan folder id (used for navigation)
  name: string
  path?: string    // VFS path for tooltip
}

/**
 * Issue #107: is a boot-load error transient (worth a bounded retry)
 * rather than terminal? Transient = the post-sign-in 401 auth window, a
 * contention 404/408/429, any 5xx, or a network drop (no HTTP status —
 * `@papan/api-client` only sets `.status` for HTTP errors). Terminal
 * (do NOT retry): a real 403/400/other-4xx — those resolve to their
 * correct state immediately and a retry would only delay it. Mirrors
 * the read-your-writes shouldRetry policy in `e2e/lib/readable.ts`, one
 * layer down in the SPA.
 */
export function isTransientLoadError(err: unknown): boolean {
  const status = (err as { status?: number } | null | undefined)?.status
  if (status === undefined) return true // network / fetch drop — no status attached
  if (status === 401 || status === 404 || status === 408 || status === 429) return true
  return status >= 500 && status <= 599
}

/**
 * Every workspace that must close to bring `list` back to `max`, coldest-first
 * per the `lastUsed` tick map. Pure + exported so the choice is testable on its
 * own (same shape as `isTransientLoadError` above) — it decides what gets
 * CLOSED, so it should not be reachable only through a React render.
 *
 * Returns a LIST, not one victim: an over-cap list must converge in a single
 * pass. Evicting one at a time means a 7-deep list opening an 8th drops back to
 * 7 and never reaches 6 — it only ever holds the line at whatever over-cap size
 * it started at.
 *
 * A workspace never touched this session reads as 0 and goes first. Ties keep
 * list order (JS sort is stable, and callers pass tab order), so the
 * oldest-opened tab loses.
 *
 * `protectedIds` is the caller's guarantee about what must survive — the
 * workspace being opened, and the active one.
 */
export function evictionVictims(
  list: OpenWorkspace[],
  lastUsed: Map<string, number>,
  protectedIds: Set<string> = new Set(),
  max: number = MAX_OPEN_WORKSPACES,
): OpenWorkspace[] {
  const overBy = list.length - max
  if (overBy <= 0) return []
  const evictable = list.filter(w => !protectedIds.has(w.folderId))
  const coldestFirst = [...evictable].sort(
    (a, b) => (lastUsed.get(a.folderId) ?? 0) - (lastUsed.get(b.folderId) ?? 0),
  )
  // Never more than exist: protecting enough entries can make the cap
  // unreachable, and half-evicting is better than throwing.
  return coldestFirst.slice(0, Math.min(overBy, coldestFirst.length))
}

interface WorkspaceManagerContextValue {
  openWorkspaces: OpenWorkspace[]
  activeWorkspaceId: string | null
  loaded: boolean
  /** Issue #107: the boot load *gave up* (a non-401 error, or 401-retry
   *  budget exhausted) — `loaded` is true but the list is NOT
   *  authoritative. Distinct from "loaded fine, workspace genuinely
   *  absent": consumers must render a recoverable state, never the
   *  "deleted / moved to bin" terminal copy, when this is true. */
  loadFailed: boolean
  /** Issue #107: re-run the boot load (recoverable affordance for a
   *  transient `loadFailed`). Also re-fires the per-folder resolver, because it
   *  toggles `loaded` false→true and that is one of the resolver's deps — so it
   *  is the recovery path for a `markLoadFailed` transient too (#675). */
  retryLoad: () => void
  /**
   * #675: folder ids whose per-folder resolve failed with a TRANSIENT error the
   * create-or-open AUTHORITY (openFolder) could not outlast — a 5xx / network
   * drop / stall, NOT a definitive 403 "gone". Recorded PER FOLDER (exactly like
   * `unresolvedFolderIds`), NOT as the global boot-list `loadFailed`: a shared
   * flag would stick after one transient and (a) suppress the terminal "may have
   * been deleted" copy for a genuinely-gone workspace opened later in the session
   * and (b) flash the recoverable error on every subsequent cold open. Consumers
   * render the recoverable "Couldn't load / Retry" state for the active folder
   * when it is in this set. The opposite-direction twin of `markUnresolved`
   * (definitive → terminal); both are cleared per-folder by `clearUnresolved`
   * (new attempt starting) and by a successful `openWorkspace`.
   */
  transientFailedFolderIds: ReadonlySet<string>
  /** Record a per-folder resolve that failed TRANSIENTLY (recoverable). */
  markLoadFailed: (folderId: string) => void
  /**
   * Folder ids we tried to resolve into a workspace and could not.
   *
   * A workspace opened from Drive is NOT in `openWorkspaces` yet — the route
   * changes first, and only then does an effect fetch its config and register
   * it. `loaded` refers to the boot list, so it is already true, which made
   * "absent from the list" look identical to "deleted" for the length of that
   * round trip: clicking a project flashed the terminal "Project not found"
   * before the workspace appeared.
   *
   * So absence is no longer the signal. Only a FAILED resolution is, which is
   * also what the copy claims. Everything else is still in flight, and the
   * caller shows a skeleton. Being a fact about a completed attempt rather
   * than a race with one, this needs no timer and cannot flash.
   */
  unresolvedFolderIds: ReadonlySet<string>
  /** Record a resolution attempt that failed — the workspace is genuinely
   *  gone or inaccessible. */
  markUnresolved: (folderId: string) => void
  /**
   * Forget an earlier failure because a NEW attempt is starting.
   *
   * A successful `openWorkspace` also clears the id, but that is too late to be
   * the only clear: a workspace that was genuinely gone and is later restored
   * from bin (or has access re-granted) stays marked, so the next click renders
   * the terminal "may have been deleted or moved to bin" copy for the whole
   * resolution round trip before snapping to the real view. That is the flash
   * this whole mechanism exists to remove, returning with confidently wrong
   * copy instead of a neutral skeleton. The mark means "the last attempt
   * failed", so a new attempt must retract it.
   */
  clearUnresolved: (folderId: string) => void
  openWorkspace: (workspace: OpenWorkspace) => void
  closeWorkspace: (folderId: string) => void
  setActiveWorkspace: (folderId: string) => void
  reorderWorkspaces: (fromIndex: number, toIndex: number) => void
  refreshWorkspaces: () => void
}

const WorkspaceManagerContext = createContext<WorkspaceManagerContextValue | null>(null)

export function WorkspaceManagerProvider({ tenantId, isReady = true, children }: { tenantId: string; isReady?: boolean; children: ReactNode }) {
  const [openWorkspaces, setOpenWorkspaces] = useState<OpenWorkspace[]>([])
  const [activeWorkspaceId, setActiveWorkspaceIdState] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  // Issue #107: distinguishes "load gave up" from "loaded fine".
  const [loadFailed, setLoadFailed] = useState(false)
  const [unresolvedFolderIds, setUnresolvedFolderIds] = useState<ReadonlySet<string>>(() => new Set())
  // #675: per-folder TRANSIENT resolve failures (recoverable). Kept separate
  // from the global boot `loadFailed` so it can't stick across folders.
  const [transientFailedFolderIds, setTransientFailedFolderIds] = useState<ReadonlySet<string>>(() => new Set())
  // Bumped by retryLoad() to re-run the boot effect.
  const [reloadNonce, setReloadNonce] = useState(0)

  // ─── Recency, for the MAX_OPEN_WORKSPACES eviction ────────────────────
  // folderId → a monotonic tick stamped every time the workspace is opened or
  // activated. A ref, not state: it must not re-render anything, and the cap
  // only ever reads it at eviction time. A counter rather than Date.now() so
  // two touches in the same millisecond still order deterministically.
  const lastUsedRef = useRef<Map<string, number>>(new Map())
  const tickRef = useRef(0)
  const touch = useCallback((folderId: string) => {
    lastUsedRef.current.set(folderId, ++tickRef.current)
  }, [])
  /** Bring a list under the cap, closing whatever it evicts. The ONE path that
   *  enforces MAX_OPEN_WORKSPACES — boot and open both go through it, so a list
   *  that arrives over-cap (opened before this cap existed) converges too
   *  rather than lingering forever at its original size. */
  const capTo = useCallback((list: OpenWorkspace[], protectedIds: Set<string> = new Set()) => {
    const victims = evictionVictims(list, lastUsedRef.current, protectedIds)
    if (victims.length === 0) return list
    const doomed = new Set(victims.map(v => v.folderId))
    for (const v of victims) {
      workspaceApi.markClosed(v.id).catch(() => {})
      lastUsedRef.current.delete(v.folderId)
    }
    return list.filter(w => !doomed.has(w.folderId))
  }, [])

  // Load open workspaces from server on mount / tenant change
  // Gated on isReady to avoid firing API calls with stale headers during tenant switch
  useEffect(() => {
    if (!isReady) return
    setLoaded(false)
    setLoadFailed(false)
    setOpenWorkspaces([]) // Clear stale workspaces from previous tenant
    setActiveWorkspaceIdState(null)
    let cancelled = false
    // Post-sign-in auth window: `/api/workspaces[/open]` can transiently
    // 401 before the session is API-effective on a proxied/contended
    // backend (trace-confirmed; same root as the realtime WS `4001
    // Unauthorized`). The old `.catch(() => setLoaded(true))` finalized
    // loaded=true with an empty list on that transient 401 and never
    // recovered → terminal "Project not found" (the agent-cost-rollup /
    // skill-gating stagetest failures). #107 broadened this: bounded
    // retry on any *transient* error (401 auth window, contention
    // 404/408/429, 5xx, network drop — see `isTransientLoadError`),
    // ~4 attempts / ~1.2s, before giving up. A genuinely-empty list,
    // or a terminal 403/400, still resolves to its correct state within
    // that small window (those are NOT retried). `.status` is provided
    // by @papan/api-client.
    ;(async () => {
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const [items, allWorkspaces] = await Promise.all([
            workspaceApi.listOpen(),
            workspaceApi.list(),
          ])
          if (cancelled) return
          // Restore active workspace: find the most recently used one that's still open
          const openIds = new Set(items.map(w => w.folderId))
          const recentSorted = [...allWorkspaces]
            .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''))
          const mostRecent = recentSorted.find(w => openIds.has(w.folderId))
          // Seed recency from the server's `updatedAt` BEFORE capping, oldest
          // first so it gets the coldest tick. Without this a boot-time
          // eviction would see an empty tick map, read every workspace as
          // equally cold, and close by raw tab order — this makes it a real
          // LRU choice. `updatedAt` is already the recency signal the
          // active-restore above trusts, so no new notion is introduced.
          for (const w of [...recentSorted].reverse()) {
            lastUsedRef.current.set(w.folderId, ++tickRef.current)
          }
          const active = mostRecent?.folderId ?? null
          setActiveWorkspaceIdState(prev => prev || active)
          // A list can arrive over-cap (opened before MAX_OPEN_WORKSPACES
          // existed, or from another device). Enforce here too, or it would
          // stay over-cap forever — `openWorkspace` alone can't claw it back,
          // it only stops it growing. Protect the one about to be shown.
          setOpenWorkspaces(capTo(items, new Set(active ? [active] : [])))
          setLoaded(true)
          return
        } catch (err) {
          if (cancelled) return
          if (isTransientLoadError(err) && attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, 200 * (attempt + 1)))
            continue
          }
          // #107: gave up — a non-transient error (real 403/400/…), or
          // the transient-retry budget exhausted. The list is NOT
          // authoritative — mark it so consumers render a recoverable
          // state, not the "deleted / moved to bin" copy.
          setLoadFailed(true)
          setLoaded(true)
          return
        }
      }
    })()
    return () => { cancelled = true }
  }, [tenantId, isReady, reloadNonce])

  const openWorkspace = useCallback((workspace: OpenWorkspace) => {
    touch(workspace.folderId)
    // A successful open clears any earlier failure — a workspace can come back
    // (restored from bin, access re-granted) and must not stay marked gone.
    // Clears BOTH the terminal (unresolved) and the transient (#675) mark.
    setUnresolvedFolderIds(prev => {
      if (!prev.has(workspace.folderId)) return prev
      const next = new Set(prev)
      next.delete(workspace.folderId)
      return next
    })
    setTransientFailedFolderIds(prev => {
      if (!prev.has(workspace.folderId)) return prev
      const next = new Set(prev)
      next.delete(workspace.folderId)
      return next
    })
    setOpenWorkspaces(prev => {
      const existing = prev.find(w => w.folderId === workspace.folderId)
      if (existing) {
        if (existing.name !== workspace.name) {
          return prev.map(w => w.folderId === workspace.folderId ? workspace : w)
        }
        return prev
      }
      // Over the cap — close the coldest until we're back under it. Never the
      // one being opened (it's the whole point of this call), and never the
      // active one (closing what you're looking at is the one unacceptable
      // eviction). markClosed here mirrors closeWorkspace's existing pattern
      // of syncing from inside the updater; it's idempotent, so a StrictMode
      // double-invoke is harmless.
      return capTo([...prev, workspace], new Set([workspace.folderId, ...(activeWorkspaceId ? [activeWorkspaceId] : [])]))
    })
    setActiveWorkspaceIdState(workspace.folderId)
    // Fire-and-forget — sync to server
    workspaceApi.markOpen(workspace.id).catch(() => {})
  }, [touch, activeWorkspaceId])

  const closeWorkspace = useCallback((folderId: string) => {
    setOpenWorkspaces(prev => {
      const ws = prev.find(w => w.folderId === folderId)
      if (ws) {
        workspaceApi.markClosed(ws.id).catch(() => {})
      }
      return prev.filter(w => w.folderId !== folderId)
    })
    setActiveWorkspaceIdState(current => current === folderId ? null : current)
  }, [])

  const setActiveWorkspace = useCallback((folderId: string) => {
    // Switching TO a workspace is a use — without this the cap would evict the
    // one you just switched to simply because it was opened long ago.
    touch(folderId)
    setActiveWorkspaceIdState(folderId)
  }, [touch])

  const reorderWorkspaces = useCallback((fromIndex: number, toIndex: number) => {
    setOpenWorkspaces(prev => {
      const next = [...prev]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      // Sync order to server
      workspaceApi.reorderOpen(next.map(w => w.id)).catch(() => {})
      return next
    })
  }, [])

  // #107: recoverable affordance for a transient `loadFailed` — bumping
  // the nonce re-runs the boot effect (which resets loadFailed/loaded).
  const retryLoad = useCallback(() => setReloadNonce(n => n + 1), [])

  // #675: a transient per-folder resolve failure surfaces the recoverable
  // "Couldn't load / Retry" state for THAT folder — recorded per-folder, NOT via
  // the global boot `loadFailed` (which would stick across folders and both
  // suppress the terminal copy for a later genuinely-gone workspace and flash on
  // every cold open). `retryLoad` (the Retry button) re-runs the boot effect,
  // whose `loaded` false→true toggle re-fires the resolver, which retracts this
  // mark via `clearUnresolved` at the start of the new attempt.
  const markLoadFailed = useCallback((folderId: string) => {
    setTransientFailedFolderIds(prev => prev.has(folderId) ? prev : new Set(prev).add(folderId))
  }, [])

  const refreshWorkspaces = useCallback(() => {
    workspaceApi.listOpen().then(serverOpen => {
      setOpenWorkspaces(serverOpen)
      // Clear active if it no longer exists
      const serverIds = new Set(serverOpen.map(w => w.folderId))
      setActiveWorkspaceIdState(prev => prev && serverIds.has(prev) ? prev : serverOpen[0]?.folderId ?? null)
    }).catch(() => {})
  }, [])

  const markUnresolved = useCallback((folderId: string) => {
    setUnresolvedFolderIds(prev => prev.has(folderId) ? prev : new Set(prev).add(folderId))
  }, [])

  // A new attempt is starting — retract BOTH the terminal and the transient
  // (#675) mark for this folder so the last attempt's outcome can't linger.
  const clearUnresolved = useCallback((folderId: string) => {
    setUnresolvedFolderIds(prev => {
      if (!prev.has(folderId)) return prev
      const next = new Set(prev)
      next.delete(folderId)
      return next
    })
    setTransientFailedFolderIds(prev => {
      if (!prev.has(folderId)) return prev
      const next = new Set(prev)
      next.delete(folderId)
      return next
    })
  }, [])

  return (
    <WorkspaceManagerContext.Provider value={{
      openWorkspaces,
      activeWorkspaceId,
      loaded,
      loadFailed,
      retryLoad,
      transientFailedFolderIds,
      markLoadFailed,
      unresolvedFolderIds,
      markUnresolved,
      clearUnresolved,
      openWorkspace,
      closeWorkspace,
      setActiveWorkspace,
      reorderWorkspaces,
      refreshWorkspaces,
    }}>
      {children}
    </WorkspaceManagerContext.Provider>
  )
}

export function useWorkspaceManager() {
  const ctx = useContext(WorkspaceManagerContext)
  if (!ctx) throw new Error('useWorkspaceManager must be used within WorkspaceManagerProvider')
  return ctx
}
